import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RowerContext } from '../../../../types/rowerContext';
import type { DisplayTab, MetricMode, ViewMode } from './utils/types';

interface UseGateForceViewModelParams {
  rowerNames: string[];
  rowersWithData: string[];
  rowerContext?: RowerContext | null;
}

export const useGateForceViewModel = ({
  rowerNames,
  rowersWithData,
  rowerContext,
}: UseGateForceViewModelParams) => {
  const isFilterMode = rowerContext?.mode === 'filter';
  const hasRowerContext = !!rowerContext;

  const focusedRowerKey = useMemo(() => {
    if (!rowerContext) return null;
    const key = `Rower ${rowerContext.position}`;
    return rowerNames.includes(key) ? key : null;
  }, [rowerContext, rowerNames]);

  const [activeTab, setActiveTab] = useState<DisplayTab>('both');
  const [currentRower, setCurrentRower] = useState(() =>
    rowerContext ? `Rower ${rowerContext.position}` : 'Rower 1'
  );
  const [selectedCrewRowers, setSelectedCrewRowers] = useState<string[]>([]);
  const [hiddenRowers, setHiddenRowers] = useState<string[]>([]);

  const availableRowers = useMemo(
    () => (rowersWithData.length ? rowersWithData : rowerNames),
    [rowerNames, rowersWithData]
  );

  const viewMode: ViewMode = useMemo(() => {
    if (activeTab === 'trace') return 'single-rower-all-strokes';
    if (activeTab === 'crew-average') return 'crew-profiles';
    return 'rower-profile';
  }, [activeTab]);

  const showStrokeOverlay: MetricMode = useMemo(() => {
    if (activeTab === 'trace') return 'traces';
    if (activeTab === 'both') return 'both';
    return 'profile';
  }, [activeTab]);

  const tracesActive = activeTab === 'trace' || activeTab === 'both';
  const averageActive =
    activeTab === 'average' || activeTab === 'both' || activeTab === 'crew-average';
  const crewActive = activeTab === 'crew-average';

  useEffect(() => {
    if (!availableRowers.length) return;
    setCurrentRower((prev) => (availableRowers.includes(prev) ? prev : availableRowers[0]));
  }, [availableRowers]);

  useEffect(() => {
    if (!rowerNames.length) {
      setSelectedCrewRowers([]);
      return;
    }
    const available = rowersWithData.length ? rowersWithData : rowerNames;
    setSelectedCrewRowers((prev) => {
      if (prev.length === 0) return prev;
      const filtered = prev.filter((rower) => available.includes(rower));
      if (prev.length === filtered.length && prev.every((r, i) => r === filtered[i])) return prev;
      return filtered;
    });
  }, [rowerNames, rowersWithData]);

  useEffect(() => {
    setHiddenRowers((prev) => {
      const filtered = prev.filter((rower) => availableRowers.includes(rower));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [availableRowers]);

  const handleTracesToggle = useCallback(() => {
    setActiveTab((prev) => {
      if (prev === 'crew-average') return 'trace';
      if (prev === 'trace') return 'average';
      if (prev === 'both') return 'average';
      return 'both';
    });
    setHiddenRowers([]);
  }, []);

  const handleAverageToggle = useCallback(() => {
    setActiveTab((prev) => {
      if (prev === 'crew-average') return 'average';
      if (prev === 'average') return 'trace';
      if (prev === 'both') return 'trace';
      return 'both';
    });
    setHiddenRowers([]);
  }, []);

  const handleCrewToggle = useCallback(() => {
    setActiveTab((prev) => {
      if (prev === 'crew-average') return 'average';
      return 'crew-average';
    });
    setHiddenRowers([]);
    setSelectedCrewRowers([]);
  }, []);

  const handleCrewRowerToggle = useCallback((rower: string) => {
    setSelectedCrewRowers((prev) => {
      if (prev.length === 0) {
        return [rower];
      }
      if (prev.includes(rower)) {
        return prev.filter((name) => name !== rower);
      }
      return [...prev, rower];
    });
  }, []);

  const handleResetFilters = useCallback(() => {
    setHiddenRowers([]);
  }, []);

  const handleLegendToggle = useCallback((rower: string) => {
    setHiddenRowers((prev) =>
      prev.includes(rower) ? prev.filter((name) => name !== rower) : [...prev, rower]
    );
  }, []);

  return {
    availableRowers,
    viewMode,
    currentRower,
    showStrokeOverlay,
    selectedCrewRowers,
    hiddenRowers,
    setCurrentRower,
    activeTab,
    tracesActive,
    averageActive,
    crewActive,
    isFilterMode,
    hasRowerContext,
    focusedRowerKey,
    handleTracesToggle,
    handleAverageToggle,
    handleCrewToggle,
    handleCrewRowerToggle,
    handleResetFilters,
    handleLegendToggle,
  };
};
